import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";

import {
  useCreateGoal,
  useDeleteGoal,
  useUpdateGoal,
  type GoalAccent,
  type GoalSummary,
} from "../api";
import { ACCENT_OPTIONS, slugify } from "../constants";

interface Props {
  year: number;
  /** Present = edit, absent = create. */
  goal?: GoalSummary | null;
  onClose: () => void;
}

const TEXTAREA_CLASS =
  "min-h-[80px] w-full resize-y rounded-xl border border-input bg-background px-3 py-2 text-sm";

export function GoalForm({ year, goal, onClose }: Props) {
  const isEdit = !!goal;
  const create = useCreateGoal();
  const update = useUpdateGoal();
  const remove = useDeleteGoal();

  const [title, setTitle] = useState(goal?.title ?? "");
  const [shortLabel, setShortLabel] = useState(goal?.short_label ?? "");
  const [tagline, setTagline] = useState(goal?.tagline ?? "");
  const [description, setDescription] = useState(goal?.description ?? "");
  const [criteria, setCriteria] = useState(goal?.success_criteria ?? "");
  const [accent, setAccent] = useState<GoalAccent>(goal?.accent ?? "power");
  const [sortOrder, setSortOrder] = useState(String(goal?.sort_order ?? 0));
  const [err, setErr] = useState<string | null>(null);

  const pending = create.isPending || update.isPending || remove.isPending;

  function submit() {
    const base = {
      title: title.trim(),
      short_label: shortLabel.trim() || null,
      tagline: tagline.trim() || null,
      description: description.trim() || null,
      success_criteria: criteria.trim() || null,
      accent,
      sort_order: parseInt(sortOrder, 10) || 0,
    };
    if (!base.title) return;
    setErr(null);
    const onError = (e: unknown) =>
      setErr(e instanceof ApiError ? e.message : "Something went wrong saving the goal.");
    if (isEdit && goal) {
      update.mutate({ id: goal.id, payload: base }, { onSuccess: onClose, onError });
    } else {
      // key is derived from the title; the backend enforces (year, key) unique.
      create.mutate({ year, key: slugify(title), ...base }, { onSuccess: onClose, onError });
    }
  }

  function del() {
    if (!goal) return;
    if (
      !window.confirm(
        `Delete the goal "${goal.title}"? Linked objectives are kept but lose the link.`,
      )
    ) {
      return;
    }
    remove.mutate(goal.id, { onSuccess: onClose });
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit goal" : "New goal"}</DialogTitle>
          <DialogDescription>
            Company goal for {year}. Objectives link to it from the Objectives page.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="goal-title">Title</Label>
            <Input
              id="goal-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Product Led Growth (PLG)"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="goal-short">Short label</Label>
              <Input
                id="goal-short"
                value={shortLabel}
                onChange={(e) => setShortLabel(e.target.value)}
                placeholder="Pillar name, e.g. Product led growth"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="goal-accent">Accent</Label>
              <select
                id="goal-accent"
                className="h-9 rounded-xl border border-input bg-background px-2 text-sm"
                value={accent}
                onChange={(e) => setAccent(e.target.value as GoalAccent)}
              >
                {ACCENT_OPTIONS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="goal-tagline">Tagline</Label>
            <Input
              id="goal-tagline"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="One line, e.g. Easy to access and start using Cinch"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="goal-description">Description</Label>
            <textarea
              id="goal-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this goal means and why it matters."
              className={TEXTAREA_CLASS}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="goal-criteria">Success criteria</Label>
            <textarea
              id="goal-criteria"
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
              placeholder="How we'll measure progress (the “Measured by…” story)."
              className={TEXTAREA_CLASS}
            />
          </div>

          <div className="flex flex-col gap-1.5 sm:max-w-[8rem]">
            <Label htmlFor="goal-sort">Sort order</Label>
            <Input
              id="goal-sort"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </div>

          {err ? <p className="text-sm font-medium text-danger">{err}</p> : null}
        </div>

        <div className="flex items-center justify-between gap-2">
          <div>
            {isEdit ? (
              <Button
                type="button"
                variant="ghost"
                className="text-danger hover:text-danger"
                onClick={del}
                disabled={pending}
              >
                Delete
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="button" onClick={submit} disabled={pending || !title.trim()}>
              {pending ? "Saving…" : isEdit ? "Save changes" : "Create goal"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
